import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, RefreshCcw, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import { addDays, format, parseISO, startOfWeek } from "date-fns";

import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  PageContainer,
  PageHeader,
  Skeleton,
  StatusPill,
  APPT_STATUS,
  EmptyState,
} from "./dealerUi";

const OutcomeDialog = ({ open, outcome, onConfirm, onCancel, saving }) => {
  const [notes, setNotes] = useState("");
  useEffect(() => { if (open) setNotes(""); }, [open]);
  if (!open) return null;
  const labels = {
    showed_up: "Mark as Showed Up",
    no_show: "Mark as No Show",
    cancelled: "Cancel appointment",
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="max-w-md" data-testid="appt-outcome-dialog">
        <DialogHeader>
          <DialogTitle>{labels[outcome]}</DialogTitle>
          <DialogDescription>
            {outcome === "showed_up"
              ? "This will count toward billing this month ($300 per showed-up appointment)."
              : outcome === "no_show"
              ? "This lead will move back to No Show. You won't be billed."
              : "The appointment will be marked cancelled and no billing will occur."}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add optional notes for your team…"
            data-testid="appt-outcome-notes-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Close</Button>
          <Button
            className="bg-brand text-white hover:bg-brand-hover"
            onClick={() => onConfirm(notes.trim())}
            disabled={saving}
            data-testid="appt-outcome-confirm-btn"
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DealerAppointments = () => {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState(null); // {apptId, kind}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = weekStart;
      const to = addDays(weekStart, 7);
      const { data } = await api.get("/dealer/appointments", {
        params: { date_from: from.toISOString(), date_to: to.toISOString() },
      });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const map = new Map(days.map((d) => [format(d, "yyyy-MM-dd"), []]));
    for (const a of rows) {
      const key = a.scheduled_at ? format(parseISO(a.scheduled_at), "yyyy-MM-dd") : null;
      if (key && map.has(key)) map.get(key).push(a);
    }
    return days.map((d) => ({ date: d, items: (map.get(format(d, "yyyy-MM-dd")) || []).sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)) }));
  }, [rows, weekStart]);

  const submitOutcome = async (notes) => {
    if (!outcome) return;
    setSaving(true);
    try {
      await api.patch(`/dealer/appointments/${outcome.apptId}/outcome`, {
        outcome: outcome.kind,
        notes: notes || undefined,
      });
      toast.success("Appointment updated");
      setOutcome(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const showedUpCount = rows.filter((a) => a.status === "showed_up").length;

  return (
    <PageContainer>
      <PageHeader
        title="Appointments"
        subtitle="Week view. Mark outcomes to keep your billing accurate."
        testId="dealer-appointments-header"
        right={
          <>
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
              <button
                className="px-3 py-2 hover:bg-slate-50 text-slate-600"
                onClick={() => setWeekStart((d) => addDays(d, -7))}
                data-testid="appt-week-prev"
                aria-label="Previous week"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="px-3 text-sm font-medium text-slate-700 whitespace-nowrap">
                {format(weekStart, "MMM d")} – {format(addDays(weekStart, 6), "MMM d, yyyy")}
              </div>
              <button
                className="px-3 py-2 hover:bg-slate-50 text-slate-600"
                onClick={() => setWeekStart((d) => addDays(d, 7))}
                data-testid="appt-week-next"
                aria-label="Next week"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="appt-this-week">
              This week
            </Button>
            <Button variant="outline" onClick={load} data-testid="appt-refresh-btn">
              <RefreshCcw size={15} /> Refresh
            </Button>
          </>
        }
      />

      <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          <span className="font-semibold text-slate-800">{rows.length}</span> appointment{rows.length === 1 ? "" : "s"} in this window
        </p>
        <p className="text-sm text-slate-500">
          <span className="font-semibold text-emerald-700">{showedUpCount}</span> showed up = {formatUSD(showedUpCount * 300)} billable
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
          {Array.from({ length: 7 }, (_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="No appointments scheduled this week"
          description="Once BDC schedules a customer visit or a lead moves to 'Appointment scheduled', it will appear here."
          testId="appt-empty-state"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3" data-testid="appt-week-grid">
          {byDay.map(({ date, items }) => {
            const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
            return (
              <div
                key={format(date, "yyyy-MM-dd")}
                className={`rounded-xl border ${
                  isToday ? "border-brand bg-brand/5" : "border-slate-200 bg-white"
                }`}
                data-testid={`appt-day-${format(date, "yyyy-MM-dd")}`}
              >
                <div className="px-3 py-2 border-b border-slate-100 text-xs">
                  <p className="uppercase tracking-widest text-slate-500 font-semibold">{format(date, "EEE")}</p>
                  <p className={`text-sm font-semibold ${isToday ? "text-brand" : "text-slate-800"}`}>
                    {format(date, "MMM d")}
                  </p>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {items.length === 0 ? (
                    <p className="text-[11px] text-slate-400 text-center py-3">No visits</p>
                  ) : (
                    items.map((a) => (
                      <div
                        key={a.id}
                        className="rounded-lg border border-slate-200 p-2 hover:border-brand transition-colors"
                        data-testid={`appt-card-${a.id}`}
                      >
                        <p className="text-xs text-slate-500 font-medium">
                          {format(parseISO(a.scheduled_at), "h:mm a")}
                        </p>
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {a.customer?.full_name || a.guest_name || "Guest"}
                        </p>
                        {a.vehicle && (
                          <p className="text-[11px] text-slate-500 truncate">
                            {a.vehicle.year} {a.vehicle.make} {a.vehicle.model}
                          </p>
                        )}
                        <StatusPill tone={APPT_STATUS[a.status]?.tone || "slate"} className="mt-1">
                          {APPT_STATUS[a.status]?.label || a.status}
                        </StatusPill>
                        {a.status === "scheduled" && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            <button
                              onClick={() => setOutcome({ apptId: a.id, kind: "showed_up" })}
                              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                              data-testid={`appt-showed-up-btn-${a.id}`}
                            >
                              <CheckCircle2 size={11} /> Show
                            </button>
                            <button
                              onClick={() => setOutcome({ apptId: a.id, kind: "no_show" })}
                              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100"
                              data-testid={`appt-no-show-btn-${a.id}`}
                            >
                              <X size={11} /> No-show
                            </button>
                            <button
                              onClick={() => setOutcome({ apptId: a.id, kind: "cancelled" })}
                              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 bg-slate-50 hover:bg-slate-100"
                              data-testid={`appt-cancel-btn-${a.id}`}
                            >
                              <XCircle size={11} /> Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <OutcomeDialog
        open={!!outcome}
        outcome={outcome?.kind}
        onConfirm={submitOutcome}
        onCancel={() => setOutcome(null)}
        saving={saving}
      />
    </PageContainer>
  );
};

export default DealerAppointments;
